/**
 * RouteErrorPage — the branded, non-technical error page that replaces
 * React Router's default developer 404. Wired as `errorElement` on
 * every top-level route in App.js so any thrown loader / rendering
 * error (401, 403, 404, 408, 409, 422, 429, 500, 502, 503, chunk-load
 * failure) surfaces the same SRES visual.
 */
import { useEffect, useMemo } from "react";
import {
  useRouteError, useNavigate, useLocation, isRouteErrorResponse, Link,
} from "react-router-dom";
import {
  ArrowClockwise, ArrowLeft, HouseSimple, Lifebuoy,
  ShieldWarning, WarningCircle, WifiHigh,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { ROUTES } from "@/config/routes";

/* ─────────────────────────────────────────────────────────────────
 * status → user-friendly message copy. NEVER expose the raw backend
 * response, stack trace, or React Router internals.
 * ────────────────────────────────────────────────────────────────*/
function copyFor(status) {
  const map = {
    401: {
      title: "Sign in required",
      body:  "Your session has expired. Please sign in again to continue.",
      icon:  ShieldWarning,
      primary: { label: "Sign in", href: ROUTES.login },
    },
    403: {
      title: "Access denied",
      body:  "You do not have permission to view this page. If you believe this is an error, please contact your administrator.",
      icon:  ShieldWarning,
      primary: { label: "Open Dashboard", href: ROUTES.dashboard },
    },
    404: {
      title: "Page not found",
      body:  "The page you requested does not exist, may have been moved, or you may not have permission to access it.",
      icon:  WarningCircle,
      primary: { label: "Open Dashboard", href: ROUTES.dashboard },
    },
    408: {
      title: "Request timed out",
      body:  "The server took too long to respond. Please check your connection and try again.",
      icon:  WifiHigh,
      primary: null,
    },
    409: {
      title: "Conflict detected",
      body:  "This action could not be completed because it conflicts with the current state of the record.",
      icon:  WarningCircle,
      primary: { label: "Open Dashboard", href: ROUTES.dashboard },
    },
    422: {
      title: "Invalid data",
      body:  "One or more values in your request could not be validated. Please review the form and try again.",
      icon:  WarningCircle,
      primary: null,
    },
    429: {
      title: "Too many requests",
      body:  "You have made too many requests in a short period. Please wait a moment and try again.",
      icon:  WifiHigh,
      primary: null,
    },
    500: {
      title: "Application error",
      body:  "An unexpected error occurred while processing your request. Our team has been notified.",
      icon:  WarningCircle,
      primary: null,
    },
    502: {
      title: "Service temporarily unavailable",
      body:  "The service is temporarily unavailable. Please retry in a few moments.",
      icon:  WifiHigh,
      primary: null,
    },
    503: {
      title: "Service temporarily unavailable",
      body:  "The service is undergoing maintenance. Please retry shortly.",
      icon:  WifiHigh,
      primary: null,
    },
  };
  return map[status] || {
    title: "Something went wrong",
    body:  "An unexpected error occurred. Please retry or return to the dashboard.",
    icon:  WarningCircle,
    primary: { label: "Open Dashboard", href: ROUTES.dashboard },
  };
}

function extractStatus(error) {
  if (!error) return 500;
  if (isRouteErrorResponse(error)) return error.status || 404;
  // Fetch / Axios shapes we might get for loader errors
  if (typeof error.status === "number") return error.status;
  if (typeof error.statusCode === "number") return error.statusCode;
  if (error.response && typeof error.response.status === "number") return error.response.status;
  // Dynamic-import failures (lazy route chunk failed to load)
  const msg = String(error.message || error);
  if (msg.includes("Failed to fetch dynamically imported module") ||
      msg.includes("Loading chunk") ||
      msg.includes("Loading CSS chunk")) return 502;
  return 500;
}

export default function RouteErrorPage() {
  const error   = useRouteError();
  const nav     = useNavigate();
  const { pathname } = useLocation();
  const status  = extractStatus(error);
  const copy    = useMemo(() => copyFor(status), [status]);
  const Icon    = copy.icon;

  // Short opaque reference for the support desk
  const ref = useMemo(() => {
    const rid = (error && error.request_id) || (error && error.data && error.data.request_id);
    if (rid) return String(rid);
    return `err-${Date.now().toString(36).slice(-6)}-${Math.random().toString(36).slice(2, 6)}`;
  }, [error]);

  // Fire a lightweight, non-blocking beacon for observability. No
  // secrets — only route, status, ref, and current pathname. The ref
  // is stable per error render and pathname/status don't change while
  // the error page is mounted, so including them as deps is safe and
  // still fires the beacon exactly once.
  useEffect(() => {
    try {
      const payload = {
        ref, status, pathname,
        ts: new Date().toISOString(),
        ua: navigator.userAgent.slice(0, 200),
      };
      const url = `${(process.env.REACT_APP_BACKEND_URL || "")}/api/logs/frontend-error`;
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob(
          [JSON.stringify(payload)], { type: "application/json" }));
      }
    } catch { /* observability must never crash the error page */ }
  }, [ref, status, pathname]);

  return (
    <div
      data-testid="route-error-page"
      data-error-status={status}
      className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center px-4 py-10"
    >
      <div className="w-full max-w-xl bg-white border border-slate-200 rounded-lg shadow-[0_1px_2px_rgba(15,23,42,0.04),0_16px_36px_-24px_rgba(15,23,42,0.16)] overflow-hidden">
        <div className="px-6 pt-6 pb-4 border-b border-slate-100 flex items-center gap-3">
          <img src="/sres-mark.png?v=6" alt="SRES" className="h-8 w-8 object-contain" />
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
              Shanvi Resources
            </span>
            <span className="text-[11px] uppercase tracking-widest text-slate-400">
              Ref&nbsp;<span data-testid="route-error-ref" className="text-slate-600 font-mono">{ref}</span>
            </span>
          </div>
        </div>

        <div className="px-6 py-8 flex flex-col items-start gap-5">
          <div className="h-12 w-12 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center">
            <Icon size={22} weight="duotone" className="text-brand-blue" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
              {status}
            </div>
            <h1
              data-testid="route-error-title"
              className="mt-2 font-heading font-black text-3xl tracking-tight text-slate-900"
            >
              {copy.title}
            </h1>
            <p
              data-testid="route-error-body"
              className="mt-3 text-[14px] text-slate-600 leading-relaxed max-w-md"
            >
              {copy.body}
            </p>
          </div>

          <div className="mt-1 flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => nav(-1)}
              data-testid="route-error-back"
              className="gap-2"
            >
              <ArrowLeft size={14} /> Go back
            </Button>
            {copy.primary && (
              <Button
                onClick={() => nav(copy.primary.href)}
                data-testid="route-error-primary"
                className="gap-2 bg-brand-blue hover:bg-brand-blue/90 text-white"
              >
                <HouseSimple size={14} /> {copy.primary.label}
              </Button>
            )}
            {status !== 404 && status !== 403 && (
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
                data-testid="route-error-retry"
                className="gap-2"
              >
                <ArrowClockwise size={14} /> Try again
              </Button>
            )}
          </div>

          <div className="pt-4 mt-2 border-t border-slate-100 w-full flex items-center gap-2 text-[12px] text-slate-500">
            <Lifebuoy size={14} className="text-slate-400" />
            <span>Need help?</span>
            <Link
              to={ROUTES.support}
              data-testid="route-error-contact"
              className="text-brand-blue hover:underline"
            >
              Contact an administrator
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
